import { useState } from 'react';
import { Loader2, ExternalLink } from 'lucide-react';
import { mapPinUrl, externalLink } from '../../lib/maps';

const SECURITY = [
  ['has_gate', 'Gated entry'],
  ['has_cameras', 'Camera coverage'],
  ['has_perimeter_fence', 'Perimeter fence'],
  ['has_onsite_staff', 'On-site staff'],
];

const SERVICES = [
  ['has_dump_station', 'Dump station'],
  ['has_wash_bay', 'Wash bay'],
  ['has_air_compressor', 'Air compressor'],
  ['has_potable_water', 'Potable water'],
];

export const BLANK_FACILITY = {
  name: '', description: '',
  address_line1: '', address_line2: '', city: '', state_code: '', postal_code: '', country_code: 'US',
  lat: '', lng: '',
  timezone: 'America/Chicago', phone: '', email: '',
  is_24h_access: false, gate_opens_at: '06:00', gate_closes_at: '21:00',
  office_opens_at: '09:00', office_closes_at: '17:00',
  has_gate: true, has_cameras: false, has_perimeter_fence: false, has_onsite_staff: false,
  has_dump_station: false, has_wash_bay: false, has_air_compressor: false, has_potable_water: false,
  amenities: [],
  tax_rate_pct: '0', admin_fee: '0', deposit: '0', late_fee: '0', late_fee_grace_days: '5',
  is_published: false,
};

/**
 * One form for creating and editing. The only difference is the slug: it is
 * derived from the name on create and frozen afterwards, because it is in
 * every public URL and every link a customer has bookmarked.
 */
export default function FacilityForm({ initial, mode, onSubmit, submitLabel, footer }) {
  const [form, setForm] = useState({ ...BLANK_FACILITY, ...initial });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [fieldError, setFieldError] = useState(null);

  const set = (key) => (event) => {
    const target = event.target;
    setForm((f) => ({ ...f, [key]: target.type === 'checkbox' ? target.checked : target.value }));
  };

  const submit = async (event) => {
    event.preventDefault();
    setBusy(true); setError(null); setFieldError(null);
    try {
      await onSubmit(serialize(form));
    } catch (err) {
      setError(err.message);
      setFieldError(err.details?.field ?? null);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } finally {
      setBusy(false);
    }
  };

  const lat = Number.parseFloat(form.lat);
  const lng = Number.parseFloat(form.lng);

  /** Accept "28.354432, -80.693074" dropped into either field. */
  const pastePair = (event) => {
    const text = (event.clipboardData?.getData('text') ?? '').trim();
    const pair = text.match(/^\s*(-?\d+(?:\.\d+)?)\s*[,\s]\s*(-?\d+(?:\.\d+)?)\s*$/);
    if (!pair) return;
    event.preventDefault();
    setForm((f) => ({ ...f, lat: pair[1], lng: pair[2] }));
  };

  /**
   * Catch the two mistakes hand-entry actually produces: a swapped pair and a
   * dropped minus sign. Both put a Florida yard in the wrong hemisphere, and
   * neither is visible without a map to look at.
   */
  const coordinateNote = (() => {
    if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null;
    if (Math.abs(lat) > 90) return { ok: false, text: 'Latitude must be between -90 and 90 — the pair may be the wrong way round.' };
    if (Math.abs(lng) > 180) return { ok: false, text: 'Longitude must be between -180 and 180.' };
    if (lng > 0 && lat > 0 && lat < 50) {
      return { ok: false, text: 'A positive longitude places this in the eastern hemisphere. Anywhere in the Americas needs a minus sign.' };
    }
    return { ok: true, text: `Placed at ${lat.toFixed(5)}, ${lng.toFixed(5)}.` };
  })();

  return (
    <form onSubmit={submit} className="space-y-6">
      {error && (
        <p role="alert" className="rounded-lg bg-rose-50 p-3 text-sm text-rose-800">{error}</p>
      )}

      <Section title="Identity">
        <label className="field-label mb-1" htmlFor="name">Facility name</label>
        <input id="name" required maxLength={180} className="field-input" value={form.name} onChange={set('name')} />
        {mode === 'create' && (
          <p className="mt-1 text-xs text-slate-500">
            The public web address is generated from this name and cannot be changed later.
          </p>
        )}

        <label className="field-label mb-1 mt-4" htmlFor="description">Description</label>
        <textarea id="description" rows={3} maxLength={5000} className="field-input"
                  value={form.description} onChange={set('description')} />
      </Section>

      <Section title="Location">
        <div className="grid gap-3 sm:grid-cols-2">
          <div className="sm:col-span-2">
            <label className="field-label mb-1" htmlFor="address_line1">Street address</label>
            <input id="address_line1" required maxLength={180} className="field-input"
                   value={form.address_line1} onChange={set('address_line1')} />
          </div>
          <div className="sm:col-span-2">
            <label className="field-label mb-1" htmlFor="address_line2">Suite / unit (optional)</label>
            <input id="address_line2" maxLength={180} className="field-input"
                   value={form.address_line2} onChange={set('address_line2')} />
          </div>
          <div>
            <label className="field-label mb-1" htmlFor="city">City</label>
            <input id="city" required maxLength={120} className="field-input" value={form.city} onChange={set('city')} />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="field-label mb-1" htmlFor="state_code">State</label>
              <input id="state_code" required maxLength={2} className="field-input uppercase"
                     value={form.state_code} onChange={set('state_code')} />
            </div>
            <div>
              <label className="field-label mb-1" htmlFor="postal_code">ZIP</label>
              <input id="postal_code" required maxLength={12} className="field-input"
                     value={form.postal_code} onChange={set('postal_code')} />
            </div>
          </div>
        </div>

        {/* The map picker is gone, so typing is the only way in. Both fields
            accept a whole "28.354432, -80.693074" pair pasted into either one,
            because that is exactly what Google Maps puts on the clipboard and
            splitting it by hand is where transcription errors come from. */}
        <p className="field-label mb-1 mt-4">Coordinates</p>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="field-label mb-1" htmlFor="lat">Latitude</label>
            <input id="lat" required inputMode="decimal" className="field-input"
                   placeholder="28.354432" value={form.lat}
                   onChange={set('lat')} onPaste={pastePair}
                   aria-invalid={fieldError === 'lat,lng' || undefined} />
          </div>
          <div>
            <label className="field-label mb-1" htmlFor="lng">Longitude</label>
            <input id="lng" required inputMode="decimal" className="field-input"
                   placeholder="-80.693074" value={form.lng}
                   onChange={set('lng')} onPaste={pastePair}
                   aria-invalid={fieldError === 'lat,lng' || undefined} />
          </div>
        </div>
        <p className="mt-1.5 text-xs text-slate-500">
          Right-click the spot in Google Maps and choose the coordinates it offers — pasting the
          whole pair into either box fills both. A facility without them is invisible to search.
        </p>
        {coordinateNote && (
          <p className={`mt-1.5 flex flex-wrap items-center gap-x-2 text-xs ${
            coordinateNote.ok ? 'text-emerald-700' : 'text-rose-700'}`}>
            {coordinateNote.text}
            {/* Without a picker there is nothing on screen that would reveal a
                plausible-looking but wrong coordinate. This is the check: one
                click confirms the pin lands on the yard and not in a field two
                towns over. */}
            {coordinateNote.ok && mapPinUrl(lat, lng) && (
              <a href={mapPinUrl(lat, lng)} {...externalLink}
                 className="inline-flex items-center gap-1 font-bold text-brand-700 hover:underline">
                Check it on the map
                <ExternalLink className="h-3 w-3" aria-hidden="true" />
                <span className="sr-only">(opens in a new tab)</span>
              </a>
            )}
          </p>
        )}

        <div className="mt-4 grid gap-3 sm:grid-cols-3">
          <div>
            <label className="field-label mb-1" htmlFor="timezone">Time zone</label>
            <input id="timezone" maxLength={64} className="field-input" value={form.timezone} onChange={set('timezone')} />
          </div>
          <div>
            <label className="field-label mb-1" htmlFor="phone">Phone</label>
            <input id="phone" type="tel" maxLength={32} className="field-input" value={form.phone} onChange={set('phone')} />
          </div>
          <div>
            <label className="field-label mb-1" htmlFor="email">Email</label>
            <input id="email" type="email" maxLength={180} className="field-input" value={form.email} onChange={set('email')} />
          </div>
        </div>
      </Section>

      <Section title="Hours">
        <label className="flex items-center gap-2 text-sm font-semibold text-slate-800">
          <input type="checkbox" className="h-4 w-4 rounded border-slate-300 text-brand-600"
                 checked={form.is_24h_access} onChange={set('is_24h_access')} />
          24-hour gate access
        </label>
        {!form.is_24h_access && (
          <div className="mt-3 grid grid-cols-2 gap-3 sm:max-w-sm">
            <div>
              <label className="field-label mb-1" htmlFor="gate_opens_at">Gate opens</label>
              <input id="gate_opens_at" type="time" className="field-input"
                     value={form.gate_opens_at ?? ''} onChange={set('gate_opens_at')} />
            </div>
            <div>
              <label className="field-label mb-1" htmlFor="gate_closes_at">Gate closes</label>
              <input id="gate_closes_at" type="time" className="field-input"
                     value={form.gate_closes_at ?? ''} onChange={set('gate_closes_at')} />
            </div>
          </div>
        )}
        <div className="mt-3 grid grid-cols-2 gap-3 sm:max-w-sm">
          <div>
            <label className="field-label mb-1" htmlFor="office_opens_at">Office opens</label>
            <input id="office_opens_at" type="time" className="field-input"
                   value={form.office_opens_at ?? ''} onChange={set('office_opens_at')} />
          </div>
          <div>
            <label className="field-label mb-1" htmlFor="office_closes_at">Office closes</label>
            <input id="office_closes_at" type="time" className="field-input"
                   value={form.office_closes_at ?? ''} onChange={set('office_closes_at')} />
          </div>
        </div>
      </Section>

      <Section title="Security and services">
        <div className="grid gap-2 sm:grid-cols-2">
          {[...SECURITY, ...SERVICES].map(([key, label]) => (
            <label key={key} className="flex items-center gap-2 text-sm text-slate-800">
              <input type="checkbox" className="h-4 w-4 rounded border-slate-300 text-brand-600"
                     checked={Boolean(form[key])} onChange={set(key)} />
              {label}
            </label>
          ))}
        </div>
      </Section>

      <Section title="Billing defaults">
        <p className="mb-3 text-xs text-slate-500">
          These seed each new lease at this facility. Changing them never touches a lease
          already signed.
        </p>
        <div className="grid gap-3 sm:grid-cols-3">
          <Money id="admin_fee" label="Admin fee" value={form.admin_fee} onChange={set('admin_fee')} />
          <Money id="deposit" label="Deposit" value={form.deposit} onChange={set('deposit')} />
          <Money id="late_fee" label="Late fee" value={form.late_fee} onChange={set('late_fee')} />
          <div>
            <label className="field-label mb-1" htmlFor="late_fee_grace_days">Grace period (days)</label>
            <input id="late_fee_grace_days" type="number" min={0} max={60} className="field-input"
                   value={form.late_fee_grace_days} onChange={set('late_fee_grace_days')} />
          </div>
          <div>
            <label className="field-label mb-1" htmlFor="tax_rate_pct">Tax rate (%)</label>
            <input id="tax_rate_pct" type="number" min={0} max={100} step="0.001" className="field-input"
                   value={form.tax_rate_pct} onChange={set('tax_rate_pct')} />
          </div>
        </div>
      </Section>

      <Section title="Visibility">
        <label className="flex items-start gap-2 text-sm text-slate-800">
          <input type="checkbox" className="mt-0.5 h-4 w-4 rounded border-slate-300 text-brand-600"
                 checked={form.is_published} onChange={set('is_published')} />
          <span>
            <strong className="font-semibold">Listed publicly</strong>
            <span className="mt-0.5 block text-xs text-slate-500">
              Unlisted facilities are invisible in search but fully usable by staff — leave this
              off until the units are loaded and the rates are right.
            </span>
          </span>
        </label>
      </Section>

      <div className="flex items-center gap-3">
        <button type="submit" disabled={busy}
                className="inline-flex items-center gap-2 rounded-lg bg-brand-600 px-5 py-2.5 text-sm font-bold text-white hover:bg-brand-700 disabled:opacity-60">
          {busy && <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />}
          {submitLabel}
        </button>
        {footer}
      </div>
    </form>
  );
}

function Section({ title, children }) {
  return (
    <section className="rounded-xl border border-slate-200 bg-white p-5">
      <h2 className="mb-3 text-sm font-bold text-slate-900">{title}</h2>
      {children}
    </section>
  );
}

function Money({ id, label, value, onChange }) {
  return (
    <div>
      <label className="field-label mb-1" htmlFor={id}>{label}</label>
      <div className="relative">
        <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-sm text-slate-500">$</span>
        <input id={id} type="number" min={0} step="0.01" className="field-input pl-7" value={value} onChange={onChange} />
      </div>
    </div>
  );
}

/** The API speaks dollars for money and plain values elsewhere; strings from
 *  inputs are fine, but empty strings must not become zeros by accident. */
function serialize(form) {
  return {
    ...form,
    lat: Number.parseFloat(form.lat),
    lng: Number.parseFloat(form.lng),
    admin_fee: form.admin_fee === '' ? 0 : Number(form.admin_fee),
    deposit: form.deposit === '' ? 0 : Number(form.deposit),
    late_fee: form.late_fee === '' ? 0 : Number(form.late_fee),
    tax_rate_pct: form.tax_rate_pct === '' ? 0 : Number(form.tax_rate_pct),
    late_fee_grace_days: form.late_fee_grace_days === '' ? 5 : Number(form.late_fee_grace_days),
  };
}
